











import { Component, Prop, Vue } from 'vue-property-decorator';
import moment from 'moment';

import {
	Bullet,
	Circle,
	Color,
	color,
	Label,
	Legend,
	Modal,
	percent,
	Root,
	Scrollbar,
	Tooltip,
} from '@amcharts/amcharts5';
import { XYChart } from '@amcharts/amcharts5/.internal/charts/xy/XYChart';
import { LineSeries } from '@amcharts/amcharts5/.internal/charts/xy/series/LineSeries';
import { DateAxis } from '@amcharts/amcharts5/.internal/charts/xy/axes/DateAxis';
import { ValueAxis } from '@amcharts/amcharts5/.internal/charts/xy/axes/ValueAxis';
import { AxisRendererX } from '@amcharts/amcharts5/.internal/charts/xy/axes/AxisRendererX';
import { AxisRendererY } from '@amcharts/amcharts5/.internal/charts/xy/axes/AxisRendererY';
import { AxisRenderer } from '@amcharts/amcharts5/.internal/charts/xy/axes/AxisRenderer';
import { XYCursor } from '@amcharts/amcharts5/.internal/charts/xy/XYCursor';

import {
	FeedApi,
	FeedTank,
	FeedTankGraphItem,
	FeedTankGraphRefillItem,
	FeedTypeLevelMetricEnum,
} from '@/api';
import { ApiUtils } from '@/common/ApiUtils';

import DatePicker from '@/components/form pickers/DatePicker.vue';
import { DateRange } from '@/models/date-range';
import { FeedTankGraphData, FeedTankUtil } from '@/pages/account/products/feed/util/feed-tank-util';

@Component({
	components: {
		DatePicker,
	},
})
export default class FeedTankVolumeChart extends Vue {
	@Prop({ required: true, type: Object })
	private feedTank!: FeedTank;

	public get hasData(): boolean {
		return (this.graphData ?? []).length > 0 || (this.refillData ?? []).length > 0;
	}

	public errorMessage: string | null = null;
	public loading: boolean = true;
	public graphData: FeedTankGraphItem[] | null = null;
	public refillData: FeedTankGraphRefillItem[] | null = null;

	private feedApi: FeedApi = new FeedApi();
	private series: LineSeries | null = null;
	private refillSeries: LineSeries | null = null;
	private noDataModal: Modal | null = null;
	private xAxis: DateAxis<AxisRenderer> | null = null;

	public async created(): Promise<void> {
		if (!this.feedTank.id) {
			this.errorMessage = 'Invalid feed tank provided';
			return;
		}
		await this.fetchGraphData(this.feedTank.id);
		if (this.hasData) {
			this.createChart(this.graphData!, this.refillData);
		}
	}

	public async onDateRangeChanged(newDateRange: Omit<DateRange, 'shortcut'>): Promise<void> {
		const startDate = moment(newDateRange.start, 'DD-MM-YYYY');
		const endDate = moment(newDateRange.end, 'DD-MM-YYYY').add(1, 'days');
		const offset = moment()
			.endOf('day')
			.diff(endDate, 'days');
		const nDays = endDate.diff(startDate, 'days') + 1;
		await this.fetchGraphData(this.feedTank.id!, offset, nDays);
		this.xAxis?.zoom(0, 1);
		this.series?.data.setAll(this.formatGraphData(this.graphData!));
		this.refillSeries?.data.setAll(this.formatRefillData(this.graphData!, this.refillData!));
		if (!this.hasData) {
			this.noDataModal?.open();
		} else {
			this.noDataModal?.close();
		}
		this.xAxis?.setAll({ min: startDate.valueOf(), max: endDate.valueOf() });
	}

	private async fetchGraphData(
		feedTankId: number,
		daysOffset: number | undefined = 0,
		nDays: number | undefined = 14,
	): Promise<void> {
		try {
			const graphDataResponse = await this.feedApi.feedTanksIdGraphDataGet(
				feedTankId,
				nDays,
				'level,logical-refill',
				daysOffset?.toString(),
			);
			this.graphData = graphDataResponse.data.data?.level ?? null;
			// @ts-ignore (this is because 'logical-refill' got auto changed in openapi generated files to "logical_refill" which is incorrect)
			this.refillData = graphDataResponse.data.data
				? graphDataResponse.data.data['logical-refill'] ?? null
				: null;
		} catch (e) {
			const errorResponse = ApiUtils.handleApiError(e, 'Failed to load graph data');
			this.errorMessage = errorResponse.errorMessage;
		} finally {
			this.loading = false;
		}
	}

	private createChart(
		graphData: FeedTankGraphItem[],
		refills: FeedTankGraphRefillItem[] | null,
	): void {
		const root: Root = Root.new('feedTankChart');
		const chart: XYChart = XYChart.new(root, {});
		const {
			cursor,
			xAxis,
			yAxis,
			series,
			refillSeries,
			legend,
			noDataModal,
		} = this.createChartFeatures(root);
		this.series = series;
		this.refillSeries = refillSeries;
		this.noDataModal = noDataModal;
		this.xAxis = xAxis;
		chart.set('cursor', cursor);
		chart.xAxes.push(xAxis);
		chart.yAxes.push(yAxis);
		chart.series.push(series);
		chart.series.push(refillSeries);

		let scrollbarX = Scrollbar.new(root, { orientation: 'horizontal' });
		chart.set('scrollbarX', scrollbarX);

		chart.bottomAxesContainer.children.push(legend);
		series.data.setAll(this.formatGraphData(graphData));
		refillSeries.data.setAll(this.formatRefillData(graphData, refills));
		legend.data.setAll(chart.series.values);
		root.container.children.push(chart);
		series.appear(1000);
		refillSeries.appear(1000);
		chart.appear(1000, 100);
	}

	private formatGraphData(
		graphData: FeedTankGraphItem[],
	): {
		primary: FeedTankGraphData;
		secondary: FeedTankGraphData;
		value: number;
		date: number;
	}[] {
		return graphData.map((item: FeedTankGraphItem) => {
			const primary: FeedTankGraphData = new FeedTankGraphData(this.feedTank, item, true);
			const secondary: FeedTankGraphData = new FeedTankGraphData(this.feedTank, item, false);
			return {
				primary,
				secondary,
				value: primary.value,
				date: new Date(item.date!).getTime(),
			};
		});
	}

	private formatRefillData(
		graphData: FeedTankGraphItem[],
		refills: FeedTankGraphRefillItem[] | null,
	): {
		primary: FeedTankGraphData;
		secondary: FeedTankGraphData;
		value: number;
		date: number;
		label: string;
	}[] {
		return (
			refills?.map((item: FeedTankGraphRefillItem) => {
				const graphDataItem: FeedTankGraphItem | null =
					graphData.find(gdv => gdv.date === item.date) ?? null;

				const graphDataItemData = new FeedTankGraphData(this.feedTank, graphDataItem ?? item, true);
				const primary: FeedTankGraphData = new FeedTankGraphData(this.feedTank, item, true);
				const secondary: FeedTankGraphData = new FeedTankGraphData(this.feedTank, item, false);

				return {
					primary,
					secondary,
					value: graphDataItemData.value,
					date: new Date(item.date!).getTime(),
					label: `Refill (${primary.text}${primary.unit})`,
				};
			}) ?? []
		);
	}

	private createChartFeatures(
		root: Root,
	): {
		xAxis: DateAxis<AxisRenderer>;
		yAxis: ValueAxis<AxisRenderer>;
		cursor: XYCursor;
		series: LineSeries;
		refillSeries: LineSeries;
		legend: Legend;
		noDataModal: Modal;
	} {
		const xAxis: DateAxis<AxisRenderer> = DateAxis.new(root, {
			baseInterval: { timeUnit: 'minute', count: 15 },
			renderer: AxisRendererX.new(root, {}),
			tooltip: Tooltip.new(root, {}),
			min: Date.now() - 86400 * 1000 * 14,
			max: Date.now(),
		});
		let nominal = (this.feedTank.nominalVolume ?? 1) * (this.feedTank.numTanks ?? 1);

		const isWeight = this.feedTank.feedtype?.levelMetric === FeedTypeLevelMetricEnum.Weight;
		if (isWeight) {
			nominal = (this.feedTank.nominalWeight ?? 1) * (this.feedTank.numTanks ?? 1);
		}
		const yAxis: ValueAxis<AxisRenderer> = ValueAxis.new(root, {
			renderer: AxisRendererY.new(root, {}),
			min: 0,
			max: nominal * 1.3,
		});
		const nominalVolumeLine = yAxis.makeDataItem({ value: nominal });
		yAxis.createAxisRange(nominalVolumeLine);
		nominalVolumeLine.get('grid')?.setAll({
			stroke: color('#e00000'),
			strokeWidth: 2,
			strokeOpacity: 1,
			strokeDasharray: [0, 3, 30],
		});
		nominalVolumeLine.get('label')?.setAll({
			text: `Nominal (${
				isWeight ? (Math.round(nominal * 0.1) * 0.01).toLocaleString() : nominal.toLocaleString()
			}${isWeight ? 't' : 'L'})`,
			inside: true,
			fill: color('#e00000'),
			dx: 15,
			fontSize: 12,
			centerX: 15,
			dy: -10,
		});
		const cursor: XYCursor = XYCursor.new(root, { behavior: 'zoomX' });
		const series: LineSeries = LineSeries.new(root, {
			name: 'Volume',
			xAxis,
			yAxis,
			tooltip: Tooltip.new(root, {
				labelText:
					'{primary.word}: {primary.text}{primary.unit} ({secondary.word}: {secondary.text}{secondary.unit})',
			}),
			showTooltipOn: 'always',
			valueXField: 'date',
			valueYField: 'value',
			connect: false,
			snapTooltip: true,
			autoGapCount: 24, // base interval of 15 minutes, if two readings are ~ 360+ minutes apart, dont draw a line between them - the sensor didn't report.
		});
		const refillSeries: LineSeries = LineSeries.new(root, {
			name: 'Refills',
			xAxis,
			yAxis,
			tooltip: Tooltip.new(root, {
				labelText:
					'{primary.word}: {primary.text}{primary.unit} ({secondary.word}: {secondary.text}{secondary.unit})',
			}),
			valueXField: 'date',
			valueYField: 'value',
			connect: false,
			snapTooltip: true,
			fill: Color.fromRGB(0, 0, 0),
		});
		refillSeries.bullets.push(r => {
			return Bullet.new(r, {
				locationX: 0.5,
				locationY: 0.5,
				sprite: Circle.new(r, {
					radius: 6,
					fill: Color.fromRGB(255, 255, 255),
					stroke: Color.fromRGB(170, 170, 170),
					strokeWidth: 2,
				}),
			});
		});
		refillSeries.bullets.push(r => {
			return Bullet.new(r, {
				sprite: Label.new(r, {
					text: '{label}',
					centerX: percent(0),
					centerY: percent(50),
					populateText: true,
					fontFamily: 'sofia-pro-soft',
					fontSize: 11,
					fontWeight: '400',
				}),
			});
		});
		const legend: Legend = Legend.new(root, {
			x: percent(50),
			centerX: percent(50),
		});
		const noDataModal: Modal = Modal.new(root, {
			content: 'No graph data for this feed tank, over this range',
		});
		return {
			xAxis,
			yAxis,
			cursor,
			series,
			refillSeries,
			legend,
			noDataModal,
		};
	}
}
